import { useEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import { Play, Minus, Plus, Copy } from "lucide-react";
import { API } from "../../lib/api";
import { textInnerStyle, resolveAnim, enterAnimCss, exitAnimCss } from "../../lib/elementStyle";
import { TimerText } from "../TimerText";
import { DynamicElement } from "../DynamicElement";
import { ChatFeed, PinnedComment, SuperChatAlert } from "../ChatElements";
import { SheetText, SheetImage } from "../SheetText";
import { ShapeElement } from "../ShapeElement";
import { t } from "../../i18n";
import { groupNameOf, groupSelId, groupColorMap, membersOf, groupBBox, groupAnimOf, movePatches, scalePatches, memberAt } from "../../lib/groups";

const SNAP = 8;

// Renders an element's inner box and plays entrance/exit animation when its
// visibility is toggled from the live panel, so the operator sees the effect in
// the preview (hidden elements settle to a dimmed, still-selectable state).
function AnimatedInner({ el, hidden, previewAnim, previewKey, isPrev, triggerKey, children }) {
  const [anim, setAnim] = useState(null);
  const [animKey, setAnimKey] = useState(0);
  const prevVis = useRef(el.visivel !== false);

  // Re-play entrance animation when a "trigger" (ativador) fires remotely/locally.
  useEffect(() => {
    if (!triggerKey || el.visivel === false) return;
    const css = enterAnimCss(el);
    if (!css) return;
    setAnim(css);
    setAnimKey((k) => k + 1);
    const a = resolveAnim(el.animacao);
    const dur = (a.entrada.duracao || 400) + (a.entrada.delay || 0) + 80;
    const t = setTimeout(() => setAnim(null), dur);
    return () => clearTimeout(t);
  }, [triggerKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const vis = el.visivel !== false;
    if (vis === prevVis.current) return;
    prevVis.current = vis;
    const css = vis ? enterAnimCss(el) : exitAnimCss(el);
    if (!css) { setAnim(null); return; }
    setAnim(css);
    setAnimKey((k) => k + 1);
    const a = resolveAnim(el.animacao);
    const dur = (vis ? (a.entrada.duracao || 400) + (a.entrada.delay || 0) : (a.saida.duracao || 400)) + 80;
    const t = setTimeout(() => setAnim(null), dur);
    return () => clearTimeout(t);
  }, [el.visivel]); // eslint-disable-line react-hooks/exhaustive-deps

  const applied = previewAnim || anim;
  const key = isPrev ? `pv-${previewKey}` : (anim ? `tg-${animKey}` : `st-${el.id}`);
  // A animação fica numa camada por fora e a rotação/opacidade numa camada por dentro:
  // assim o "transform"/"opacity" da animação não apaga a rotação nem a opacidade da camada.
  return (
    <div key={key} style={{ width: "100%", height: "100%", mixBlendMode: el.blendMode || "normal", ...(applied || {}) }}>
      <div
        style={{
          width: "100%", height: "100%",
          transform: `rotate(${el.rotacao || 0}deg)`,
          opacity: hidden ? 0.25 : (el.opacidade ?? 100) / 100,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function computeSnap(el, px, py, others, dims) {
  const w = el.largura, h = el.altura;
  const xTargets = [0, dims.w / 2, dims.w];
  const yTargets = [0, dims.h / 2, dims.h];
  others.forEach((o) => {
    xTargets.push(o.x, o.x + o.largura / 2, o.x + o.largura);
    yTargets.push(o.y, o.y + o.altura / 2, o.y + o.altura);
  });
  let sx = px, gx = null, bx = SNAP + 0.001;
  const xe = [px, px + w / 2, px + w];
  for (const t of xTargets) for (const e of xe) {
    const d = Math.abs(e - t);
    if (d < bx) { bx = d; sx = px + (t - e); gx = t; }
  }
  let sy = py, gy = null, by = SNAP + 0.001;
  const ye = [py, py + h / 2, py + h];
  for (const t of yTargets) for (const e of ye) {
    const d = Math.abs(e - t);
    if (d < by) { by = d; sy = py + (t - e); gy = t; }
  }
  return { x: Math.round(sx), y: Math.round(sy), gx, gy };
}

function ElementContent({ el, media, ytData, twitchData, sheetValues }) {
  const bound = el.sheetBind?.enabled;
  const sv = sheetValues?.[el.id];
  if (bound) {
    const first = Array.isArray(sv) ? sv[0] : sv;
    const looksImg = first && /^https?:\/\//i.test(String(first));
    if (el.tipo === "media" || looksImg) return <SheetImage el={el} values={sv} editor />;
    return <SheetText el={el} values={sv} />;
  }
  if (el.tipo === "media") {
    const m = media?.[el.mediaId];
    if (!el.mediaId) return null;
    const src = `${API}/media/${el.mediaId}/file`;
    const isVideo = m ? m.kind === "video" : true;
    return isVideo ? (
      <video key={`v-${el.autoplay !== false}-${el.loop !== false}`} src={src}
        autoPlay={el.autoplay !== false} muted loop={el.loop !== false} playsInline
        style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }} />
    ) : (
      <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }} />
    );
  }
  if (el.tipo === "dynamic") {
    return <DynamicElement el={el} ytData={ytData} twitchData={twitchData} />;
  }
  if (el.tipo === "chat") {
    return <ChatFeed el={el} preview />;
  }
  if (el.tipo === "comment") {
    return <PinnedComment el={el} preview />;
  }
  if (el.tipo === "superchat") {
    return <SuperChatAlert el={el} preview />;
  }
  if (el.tipo === "forma") {
    return <ShapeElement el={el} />;
  }
  if (el.papel === "cronometro") {
    return <TimerText timer={el.timer} style={textInnerStyle(el.estilo || {})} />;
  }
  return <div style={textInnerStyle(el.estilo || {})}>{el.conteudo}</div>;
}

export function Canvas({ elements, media, dims, selectedId, onSelect, multiIds = [], onToggleMulti, onChange, onChangeMany, onDuplicate, ytData, twitchData, sheetValues, triggerKeys }) {
  const wrapRef = useRef(null);
  const [fitScale, setFitScale] = useState(0.4);
  const [zoomMult, setZoomMult] = useState(1);
  const scale = fitScale * zoomMult;
  const [dragId, setDragId] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [guides, setGuides] = useState({ gx: null, gy: null });
  const [preview, setPreview] = useState({ phase: null, key: 0, group: null });
  const [gDrag, setGDrag] = useState(null); // arraste do grupo: { name, dx, dy }
  const boardRef = useRef(null);
  // Seleção múltipla (Shift+clique): com Shift apertado as camadas não arrastam, só entram/saem da seleção.
  const [shiftHeld, setShiftHeld] = useState(false);
  useEffect(() => {
    const dn = (e) => e.key === "Shift" && setShiftHeld(true);
    const up = (e) => e.key === "Shift" && setShiftHeld(false);
    const blur = () => setShiftHeld(false);
    window.addEventListener("keydown", dn); window.addEventListener("keyup", up); window.addEventListener("blur", blur);
    return () => { window.removeEventListener("keydown", dn); window.removeEventListener("keyup", up); window.removeEventListener("blur", blur); };
  }, []);
  const multiMembers = multiIds.length > 1 ? elements.filter((x) => multiIds.includes(x.id)) : [];

  // Grupos: clicar numa camada de grupo seleciona o grupo inteiro; duplo clique entra no grupo
  // e seleciona a camada (aí ela é editada sozinha, como antes).
  const selGroup = groupNameOf(selectedId);
  const selEl = elements.find((e) => e.id === selectedId);
  const diveGroup = selEl?.grupo || null;
  const colors = groupColorMap(elements);
  const inGroupMode = (el) => !!el.grupo && el.grupo !== diveGroup;

  // Ligar/desligar um grupo no Ao Vivo: toca a animação do grupo no canvas (as camadas tocam as suas).
  const [liveG, setLiveG] = useState({}); // { [grupo]: { phase, key } }
  const prevGroupVis = useRef(null);
  const groupVisKey = [...new Set(elements.map((e) => e.grupo).filter(Boolean))]
    .map((g) => `${g}:${elements.some((e) => e.grupo === g && e.visivel !== false) ? 1 : 0}`).join("|");
  useEffect(() => {
    const now = Object.fromEntries(groupVisKey ? groupVisKey.split("|").map((x) => { const i = x.lastIndexOf(":"); return [x.slice(0, i), x.slice(i + 1) === "1"]; }) : []);
    const prev = prevGroupVis.current;
    prevGroupVis.current = now;
    if (!prev) return;
    for (const [g, vis] of Object.entries(now)) {
      if (prev[g] === undefined || prev[g] === vis) continue;
      const anim = groupAnimOf(membersOf(elements, g));
      const fake = { animacao: anim };
      const css = vis ? enterAnimCss(fake) : exitAnimCss(fake);
      if (!css || !css.animationName) continue;
      const a = resolveAnim(anim);
      const dur = (vis ? (a.entrada.duracao || 400) + (a.entrada.delay || 0) : (a.saida.duracao || 400)) + 80;
      const key = Date.now();
      setLiveG((o) => ({ ...o, [g]: { phase: vis ? "in" : "out", key } }));
      setTimeout(() => setLiveG((o) => (o[g]?.key === key ? (({ [g]: _, ...rest }) => rest)(o) : o)), dur);
    }
  }, [groupVisKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pan, setPan] = useState({ x: 0, y: 0 });

  const startPan = (e) => {
    // Inicia pan no fundo do wrap OU na área vazia do board (.checker-bg),
    // que cobre o wrap quando há zoom. Não dispara sobre elementos (Rnd).
    if (!(e.target === e.currentTarget || e.target.classList?.contains("checker-bg"))) return;
    const startX = e.clientX, startY = e.clientY;
    const orig = { ...pan };
    let moved = false;
    const onMove = (ev) => {
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
      setPan({ x: orig.x + dx, y: orig.y + dy });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (!moved) onSelect(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const previewSel = () => {
    if (!selectedId) return;
    if (selGroup) {
      // Grupo: a animação do grupo e as animações de cada camada rodam juntas.
      const gm = membersOf(elements, selGroup);
      const items = [{ animacao: groupAnimOf(gm) }, ...gm];
      const inDur = Math.max(...items.map((x) => { const a = resolveAnim(x.animacao).entrada; return a.efeito === "none" ? 0 : (a.duracao || 0) + (a.delay || 0); })) + 200;
      const hasOut = items.some((x) => exitAnimCss(x));
      const outDur = Math.max(...items.map((x) => (exitAnimCss(x) ? resolveAnim(x.animacao).saida.duracao || 400 : 0)));
      setPreview({ phase: "in", key: Date.now(), group: selGroup });
      if (hasOut) {
        setTimeout(() => setPreview({ phase: "out", key: Date.now(), group: selGroup }), inDur + 600);
        setTimeout(() => setPreview({ phase: null, key: 0, group: null }), inDur + 600 + outDur + 100);
      } else {
        setTimeout(() => setPreview({ phase: null, key: 0, group: null }), inDur + 100);
      }
      return;
    }
    const el = elements.find((e) => e.id === selectedId);
    if (!el) return;
    const { entrada, saida } = resolveAnim(el.animacao);
    setPreview({ phase: "in", key: Date.now(), group: null });
    const inDur = (entrada.duracao || 0) + (entrada.delay || 0) + 200;
    if (exitAnimCss(el)) {
      setTimeout(() => setPreview({ phase: "out", key: Date.now(), group: null }), inDur + 600);
      setTimeout(() => setPreview({ phase: null, key: 0, group: null }), inDur + 600 + (saida.duracao || 400) + 100);
    } else {
      setTimeout(() => setPreview({ phase: null, key: 0, group: null }), inDur + 100);
    }
  };

  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const { clientWidth, clientHeight } = wrapRef.current;
      const s = Math.min((clientWidth - 48) / dims.w, (clientHeight - 48) / dims.h);
      setFitScale(s > 0 ? s : 0.1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [dims]);

  // Atalhos de zoom: Ctrl/Cmd + "+" | "-" | "0" (não interfere em campos de texto)
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const t = e.target.tagName;
      if (t === "INPUT" || t === "TEXTAREA" || t === "SELECT" || e.target.isContentEditable) return;
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setZoomMult((z) => Math.min(4, +(z * 1.2).toFixed(3)));
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        setZoomMult((z) => Math.max(0.2, +(z / 1.2).toFixed(3)));
      } else if (e.key === "0") {
        e.preventDefault();
        setZoomMult(1);
        setPan({ x: 0, y: 0 });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Zoom com Ctrl/Cmd + roda do mouse (como em editores profissionais).
  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      setZoomMult((z) => {
        const next = e.deltaY < 0 ? z * 1.1 : z / 1.1;
        return Math.min(4, Math.max(0.2, +next.toFixed(3)));
      });
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, []);

  const lineW = 1 / scale;

  return (
    <div
      ref={wrapRef}
      className="flex-1 flex items-center justify-center overflow-hidden bg-[#2b2c33] relative cursor-grab active:cursor-grabbing"
      onMouseDown={startPan}
      data-testid="studio-canvas"
    >
      <div className="absolute top-3 left-3 z-50 flex items-center gap-2">
        <button
          onClick={previewSel}
          disabled={!selectedId}
          className="h-8 px-3 rounded-sm bg-primary/90 text-primary-foreground text-xs font-semibold flex items-center gap-1.5 hover:bg-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="preview-animation-btn"
        >
          <Play className="h-3.5 w-3.5" /> {t("Prever animação")}
        </button>
        <button
          onClick={() => selectedId && onDuplicate?.(selectedId)}
          disabled={!selectedId}
          className="h-8 px-3 rounded-sm bg-secondary/90 border border-border/60 text-foreground text-xs font-semibold flex items-center gap-1.5 hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title={t("Duplicar elemento selecionado (Ctrl+C, Ctrl+V)")}
          data-testid="canvas-duplicate-btn"
        >
          <Copy className="h-3.5 w-3.5" /> {t("Duplicar")}
        </button>
      </div>
      <div
        className="absolute top-3 right-3 z-50 h-8 px-2.5 rounded-sm bg-black/60 border border-border/60 text-[11px] font-mono flex items-center gap-1.5 text-muted-foreground"
        title={t("Zoom: Ctrl + / Ctrl - / Ctrl 0 · Ctrl + roda do mouse")}
        data-testid="canvas-zoom-label"
      >
        {/* "reset" fica à esquerda para os botões − / + não mudarem de lugar ao aparecer */}
        {zoomMult !== 1 && (
          <button onClick={() => { setZoomMult(1); setPan({ x: 0, y: 0 }); }} className="text-primary hover:underline mr-0.5" title={t("Voltar ao zoom ajustado à tela")} data-testid="canvas-zoom-reset">reset</button>
        )}
        <button
          onClick={() => setZoomMult((z) => Math.max(0.2, +(z / 1.2).toFixed(3)))}
          className="h-5 w-5 flex items-center justify-center rounded-sm hover:bg-white/10 text-foreground"
          title={t("Diminuir zoom")}
          data-testid="canvas-zoom-out"
        ><Minus className="h-3 w-3" /></button>
        <span className="min-w-[34px] text-center text-foreground" data-testid="canvas-zoom-value">{Math.round(zoomMult * 100)}%</span>
        <button
          onClick={() => setZoomMult((z) => Math.min(4, +(z * 1.2).toFixed(3)))}
          className="h-5 w-5 flex items-center justify-center rounded-sm hover:bg-white/10 text-foreground"
          title={t("Aumentar zoom")}
          data-testid="canvas-zoom-in"
        ><Plus className="h-3 w-3" /></button>
      </div>
      <div
        className="checker-bg absolute"
        style={{
          width: dims.w, height: dims.h,
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`, transformOrigin: "center center",
          left: "50%", top: "50%", marginLeft: -dims.w / 2, marginTop: -dims.h / 2,
          boxShadow: "0 0 0 1px rgba(0,0,0,0.55), 0 12px 40px rgba(0,0,0,0.45)",
        }}
        ref={boardRef}
      >
        {(() => {
          const sorted = [...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
          const renderEl = (el, extraStyle, forced) => {
            const selected = el.id === selectedId;
            const hidden = el.visivel === false;
            const others = elements.filter((x) => x.id !== el.id);
            // forced: a camada foi para dentro do invólucro do grupo e precisa tocar a própria animação.
            const isPrev = !!forced || (!!preview.phase && (preview.group ? el.grupo === preview.group : selected));
            const phaseNow = forced ? forced.phase : preview.phase;
            const previewAnim = isPrev ? (phaseNow === "in" ? enterAnimCss(el) : exitAnimCss(el)) : null;
            const tk = triggerKeys?.[el.id] || 0;
            const inMulti = multiMembers.length > 1 && multiIds.includes(el.id);
            const gMode = inGroupMode(el) && !inMulti;
            const gColor = el.grupo ? colors[el.grupo] : null;
            const members = el.grupo ? membersOf(elements, el.grupo) : [];
            const pos = gDrag && (gDrag.multi ? inMulti : el.grupo === gDrag.name)
              ? { x: el.x + gDrag.dx, y: el.y + gDrag.dy }
              : (dragId === el.id && dragPos ? dragPos : { x: el.x, y: el.y });
            const outline = selected || inMulti ? "2px solid #007AFF"
              : gColor && (selGroup === el.grupo || diveGroup === el.grupo) ? `1px dashed ${gColor}`
                : "1px dashed rgba(255,255,255,0.18)";
            return (
              <Rnd
                key={el.id}
                size={{ width: el.largura, height: el.altura }}
                position={pos}
                scale={scale}
                disableDragging={shiftHeld || el.travado || (gMode && members.some((m) => m.travado)) || (inMulti && multiMembers.some((m) => m.travado))}
                enableResizing={!el.travado && !gMode && !inMulti}
                onMouseDown={(e) => {
                  if (e.shiftKey) { onToggleMulti?.(el.id); return; }
                  if (inMulti) return; // arrastar uma camada da seleção move todas
                  onSelect(gMode ? groupSelId(el.grupo) : el.id);
                }}
                onDragStart={() => {
                  if (inMulti) { setGDrag({ multi: true, dx: 0, dy: 0 }); return; }
                  if (gMode) { onSelect(groupSelId(el.grupo)); setGDrag({ name: el.grupo, dx: 0, dy: 0 }); return; }
                  onSelect(el.id); setDragId(el.id);
                }}
                onDrag={(e, d) => {
                  if (inMulti) { setGDrag({ multi: true, dx: d.x - el.x, dy: d.y - el.y }); return; }
                  if (gMode) { setGDrag({ name: el.grupo, dx: d.x - el.x, dy: d.y - el.y }); return; }
                  const r = computeSnap(el, d.x, d.y, others, dims);
                  setDragPos({ x: r.x, y: r.y });
                  setGuides({ gx: r.gx, gy: r.gy });
                }}
                onDragStop={(e, d) => {
                  if (inMulti) {
                    const dx = d.x - el.x, dy = d.y - el.y;
                    if (dx || dy) onChangeMany?.(movePatches(multiMembers, dx, dy));
                    setGDrag(null);
                    return;
                  }
                  if (gMode) {
                    const dx = d.x - el.x, dy = d.y - el.y;
                    if (dx || dy) onChangeMany?.(movePatches(members, dx, dy));
                    setGDrag(null);
                    return;
                  }
                  const r = computeSnap(el, d.x, d.y, others, dims);
                  onChange(el.id, { x: r.x, y: r.y }, { sendWs: true });
                  setDragId(null);
                  setDragPos(null);
                  setGuides({ gx: null, gy: null });
                }}
                onResizeStop={(e, dir, ref, delta, p) =>
                  onChange(el.id, {
                    largura: Math.round(ref.offsetWidth),
                    altura: Math.round(ref.offsetHeight),
                    x: Math.round(p.x), y: Math.round(p.y),
                  }, { sendWs: true })
                }
                style={{ zIndex: el.zIndex || 0, outline, outlineOffset: 0, ...(extraStyle || {}) }}
                data-testid={`canvas-el-${el.id}`}
              >
                <AnimatedInner el={el} hidden={hidden} previewAnim={previewAnim} previewKey={forced ? forced.key : preview.key} isPrev={isPrev} triggerKey={tk}>
                  <ElementContent el={el} media={media} ytData={ytData} twitchData={twitchData} sheetValues={sheetValues} />
                </AnimatedInner>
              </Rnd>
            );
          };

          // Animação do grupo (prévia ou ligar/desligar no Ao Vivo): as camadas do grupo ficam num
          // invólucro animado, e cada camada continua tocando a sua própria animação ao mesmo tempo.
          const wrapped = {};
          if (preview.group && preview.phase) wrapped[preview.group] = { phase: preview.phase, key: `pv-${preview.key}` };
          for (const [g, v] of Object.entries(liveG)) if (!wrapped[g]) wrapped[g] = { phase: v.phase, key: `lv-${v.key}`, live: true };
          if (!Object.keys(wrapped).length) return sorted.map((el) => renderEl(el));
          const out = [];
          const done = new Set();
          for (const el of sorted) {
            if (!el.grupo || !wrapped[el.grupo]) { out.push(renderEl(el)); continue; }
            if (done.has(el.grupo)) continue;
            done.add(el.grupo);
            const w = wrapped[el.grupo];
            const gm = sorted.filter((e) => e.grupo === el.grupo);
            const bb = groupBBox(gm);
            const fake = { animacao: groupAnimOf(gm) };
            const anim = w.phase === "in" ? enterAnimCss(fake) : exitAnimCss(fake);
            // Ordem visual: um invólucro por camada, cada um no z-index dela (todos animam ao mesmo tempo).
            for (const m of gm) {
              out.push(
                <div key={`gw-${el.grupo}-${m.id}-${w.key}`} style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: m.zIndex || 0, transformOrigin: `${bb.x + bb.w / 2}px ${bb.y + bb.h / 2}px`, ...(anim || {}) }}>
                  {renderEl(m, { pointerEvents: "auto" }, w.live ? { phase: w.phase, key: w.key } : null)}
                </div>
              );
            }
          }
          return out;
        })()}

        {/* Máscara fora do quadro: o que sai da área do overlay fica escondido (aparece só como um
            fantasma bem apagado, para ainda ser possível pegar e trazer o elemento de volta). */}
        <div
          aria-hidden="true"
          style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 99980, boxShadow: "0 0 0 100000px rgba(43,44,51,0.9)" }}
          data-testid="canvas-outside-mask"
        />
        {/* Contorno da camada selecionada por cima da máscara (visível mesmo fora do quadro). */}
        {selEl && (() => {
          const p = dragId === selEl.id && dragPos ? dragPos : { x: selEl.x, y: selEl.y };
          return (
            <div
              aria-hidden="true"
              style={{ position: "absolute", left: p.x, top: p.y, width: selEl.largura, height: selEl.altura, outline: `${2 / scale}px solid #007AFF`, pointerEvents: "none", zIndex: 99985 }}
              data-testid="canvas-selection-outline"
            />
          );
        })()}

        {/* Caixa do grupo selecionado: arrastar move tudo; os cantos redimensionam proporcionalmente. */}
        {selGroup && (() => {
          const gm = membersOf(elements, selGroup);
          if (!gm.length) return null;
          const bb = groupBBox(gm);
          const color = colors[selGroup] || "#007AFF";
          const locked = gm.some((m) => m.travado);
          const pos = gDrag && gDrag.name === selGroup ? { x: bb.x + gDrag.dx, y: bb.y + gDrag.dy } : { x: bb.x, y: bb.y };
          return (
            <Rnd
              key={`gbox-${selGroup}-${bb.x}-${bb.y}-${bb.w}-${bb.h}`}
              size={{ width: bb.w, height: bb.h }}
              position={pos}
              scale={scale}
              lockAspectRatio
              disableDragging={locked}
              onDragStart={() => setGDrag({ name: selGroup, dx: 0, dy: 0 })}
              onDrag={(e, d) => setGDrag({ name: selGroup, dx: d.x - bb.x, dy: d.y - bb.y })}
              onDragStop={(e, d) => {
                const dx = d.x - bb.x, dy = d.y - bb.y;
                if (dx || dy) onChangeMany?.(movePatches(gm, dx, dy));
                setGDrag(null);
              }}
              onResizeStop={(e, dir, ref, delta, p) => {
                const to = { x: Math.round(p.x), y: Math.round(p.y), w: ref.offsetWidth, h: ref.offsetHeight };
                if (to.w !== bb.w || to.h !== bb.h || to.x !== bb.x || to.y !== bb.y) onChangeMany?.(scalePatches(gm, bb, to));
              }}
              resizeHandleStyles={Object.fromEntries(["topLeft", "topRight", "bottomLeft", "bottomRight"].map((k) => [k, { width: 14 / scale, height: 14 / scale, background: color, border: `${1 / scale}px solid #fff`, borderRadius: 2 / scale }]))}
              enableResizing={locked ? false : { top: false, right: false, bottom: false, left: false, topLeft: true, topRight: true, bottomLeft: true, bottomRight: true }}
              style={{ zIndex: 99990, outline: `${2 / scale}px solid ${color}`, background: `${color}10` }}
              data-testid="canvas-group-box"
            >
              <div
                className="w-full h-full"
                onDoubleClick={(e) => {
                  const rect = boardRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  const px = (e.clientX - rect.left) / scale, py = (e.clientY - rect.top) / scale;
                  const hit = memberAt(gm, px, py);
                  if (hit) onSelect(hit.id);
                }}
              />
              <div style={{ position: "absolute", left: 0, top: -24 / scale, fontSize: 11 / scale, lineHeight: `${18 / scale}px`, padding: `0 ${6 / scale}px`, background: color, color: "#0a0a0c", fontWeight: 700, borderRadius: 3 / scale, whiteSpace: "nowrap", pointerEvents: "none" }}>
                {selGroup} · {t("duplo clique para editar uma camada")}
              </div>
            </Rnd>
          );
        })()}

        {/* Caixa da seleção múltipla (só visual: o arraste é feito nas próprias camadas) */}
        {multiMembers.length > 1 && (() => {
          const bb = groupBBox(multiMembers);
          const pos = gDrag?.multi ? { x: bb.x + gDrag.dx, y: bb.y + gDrag.dy } : { x: bb.x, y: bb.y };
          return (
            <div style={{ position: "absolute", left: pos.x, top: pos.y, width: bb.w, height: bb.h, outline: `${2 / scale}px dashed #007AFF`, background: "#007AFF10", zIndex: 99990, pointerEvents: "none" }} data-testid="canvas-multi-box">
              <div style={{ position: "absolute", left: 0, top: -24 / scale, fontSize: 11 / scale, lineHeight: `${18 / scale}px`, padding: `0 ${6 / scale}px`, background: "#007AFF", color: "#fff", fontWeight: 700, borderRadius: 3 / scale, whiteSpace: "nowrap" }}>
                {t("{n} camadas selecionadas", { n: multiMembers.length })}
              </div>
            </div>
          );
        })()}

        {guides.gx != null && (
          <div style={{ position: "absolute", left: guides.gx, top: 0, width: lineW, height: dims.h, background: "#007AFF", zIndex: 99999, pointerEvents: "none" }} data-testid="guide-vertical" />
        )}
        {guides.gy != null && (
          <div style={{ position: "absolute", top: guides.gy, left: 0, height: lineW, width: dims.w, background: "#007AFF", zIndex: 99999, pointerEvents: "none" }} data-testid="guide-horizontal" />
        )}
      </div>
    </div>
  );
}
